// frontend/src/pages/JefeDashboard/JefeTareas.js
import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom';
import { FaClipboardCheck, FaHourglassHalf, FaUserCheck, FaBan, FaSpinner, FaCheck, FaTimes, FaInfoCircle, FaUser, FaCalendar, FaProjectDiagram, FaPaperclip, FaDownload, FaExternalLinkAlt, FaCheckCircle, FaExclamationCircle, FaChevronDown, FaChevronUp, FaTasks } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';

export default function JefeTareas() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [taskDetail, setTaskDetail] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [busquedaProyecto, setBusquedaProyecto] = useState('');
  const [filtroColaborador, setFiltroColaborador] = useState('');
  const [revisionDate, setRevisionDate] = useState('');
  const [showRevisionDate, setShowRevisionDate] = useState(false);
  const [acceptingDate, setAcceptingDate] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [solLoading, setSolLoading] = useState(false);
  const [solError, setSolError] = useState(null);
  const [comentariosSolicitud, setComentariosSolicitud] = useState({});
  const [respondingId, setRespondingId] = useState(null);
  const location = useLocation();
  const [toast, setToast] = useState({ visible: false, type: 'success', title: '', message: '' });
  const [approveComment, setApproveComment] = useState('');
  const [collapsedProjects, setCollapsedProjects] = useState({});
  const [revisiones, setRevisiones] = useState([]);
  const [revLoading, setRevLoading] = useState(false);
  const [revError, setRevError] = useState(null);

  useEffect(() => {
    if (!toast.visible) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.visible]);

  // Calcular estadísticas
  const stats = {
    total: tasks.length,
    enRevision: tasks.filter(t => t.estado === 'EN_REVISION').length,
    aprobadas: tasks.filter(t => t.estado === 'APROBADO').length,
    rechazadas: tasks.filter(t => t.estado === 'RECHAZADO').length,
  };

  useEffect(() => {
    cargarTareas();
    cargarSolicitudes();
    cargarRevisiones();
  }, []);

  const cargarTareas = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAuth('/api/jefe/tareas/');
      setTasks(data.tareas || []);
    } catch (err) {
      console.error('Error cargando tareas:', err);
      setError('No se pudieron cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const cargarSolicitudes = async () => {
    try {
      setSolLoading(true);
      setSolError(null);
      const data = await fetchAuth('/api/jefe/solicitudes-plazo/');
      setSolicitudes(data.solicitudes || []);
    } catch (err) {
      console.error('Error cargando solicitudes de plazo:', err);
      setSolError('No se pudieron cargar las solicitudes de plazo');
    } finally {
      setSolLoading(false);
    }
  };

  const cargarRevisiones = async () => {
    try {
      setRevLoading(true);
      setRevError(null);
      const data = await fetchAuth('/api/jefe/revisiones/');
      setRevisiones(Array.isArray(data?.revisiones) ? data.revisiones : []);
    } catch (err) {
      setRevError('No se pudieron cargar las revisiones');
    } finally {
      setRevLoading(false);
    }
  };

  const responderSolicitud = async (id, decision) => {
    try {
      setRespondingId(id);
      const comentario = comentariosSolicitud[id] || '';
      await fetchAuth(`/api/jefe/solicitudes-plazo/${id}/responder/`, {
        method: 'POST',
        body: JSON.stringify({ decision, comentario })
      });
      const sol = solicitudes.find(s => s.id === id);
      if (sol && (decision === 'APROBADO' || decision === 'ACEPTADA' || decision === 'APROBADA')) {
        setTasks(prev => prev.map(t => t.id === sol.id_tarea ? { ...t, fecha_plazo: sol.nueva_fecha, estado_plazo: 'APROBADO', estado: 'EN_PROGRESO' } : t));
      }
      setSolicitudes(prev => prev.filter(s => s.id !== id));
      const accepted = (decision === 'APROBADO' || decision === 'ACEPTADA' || decision === 'APROBADA');
      setToast({
        visible: true,
        type: 'success',
        title: accepted ? 'Solicitud aceptada' : 'Solicitud rechazada',
        message: accepted ? 'Se aceptó la nueva fecha de entrega.' : 'Se rechazó la solicitud de plazo.'
      });
    } catch (err) {
      console.error('Error respondiendo solicitud:', err);
      setToast({ visible: true, type: 'error', title: 'No se pudo responder', message: err.message || 'Intenta nuevamente.' });
    } finally {
      setRespondingId(null);
    }
  };

  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const openTaskId = params.get('openTask');
    if (!openTaskId || !tasks.length) return;
    const task = tasks.find(t => String(t.id) === String(openTaskId));
    if (task) {
      handleVerDetalle(task);
    }
  }, [loading, location.search, tasks]);

  // Filtrar tareas (excluir Onboarding colaborador)
  const esOnboarding = (t) => {
    const n = String(t?.nombre || '').toLowerCase();
    const d = String(t?.descripcion || '').toLowerCase();
    return (
      n.includes('onboarding') ||
      d.includes('onboarding') ||
      n.includes('solicitud de plazo') ||
      d.includes('solicitud de plazo') ||
      n.includes('extension de plazo') ||
      d.includes('extension de plazo') ||
      n.includes('extensión de plazo') ||
      d.includes('extensión de plazo') ||
      n.includes('ampliación de plazo') ||
      d.includes('ampliación de plazo')
    );
  };
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const colaboradoresUnicos = Array.from(new Set(tasks.map(t => String(t.colaborador || '').trim()).filter(Boolean))).sort(new Intl.Collator('es').compare);
  const proyectosUnicos = Array.from(new Set(tasks.map(t => String(t.proyecto?.nombre || '').trim()).filter(Boolean))).sort(new Intl.Collator('es').compare);
  const estadoOptions = [
    { value: 'TODOS', label: 'Todos los estados' },
    { value: 'EN_REVISION', label: 'En Revisión' },
    { value: 'APROBADO', label: 'Aprobado' },
    { value: 'RECHAZADO', label: 'Rechazado' },
    { value: 'EN_PROGRESO', label: 'En Progreso' },
    { value: 'PENDIENTE', label: 'Pendiente' }
  ];

  const SearchableSelect = ({ value, onChange, options, placeholder, className }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);
    useEffect(() => {
      const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', h);
      return () => document.removeEventListener('mousedown', h);
    }, []);
    const filtered = options.filter(o => norm(o.label).includes(norm(query)));
    const selected = options.find(o => o.value === value);
    return (
      <div ref={ref} className="relative">
        <button type="button" className={`px-3 py-2 border border-slate-300 rounded-lg text-left flex items-center justify-between ${className || 'min-w-[220px]'}`} onClick={() => setOpen(v => !v)}>
          <span>{selected ? selected.label : placeholder}</span>
          <span className="text-slate-400">▾</span>
        </button>
        {open && (
          <div className="absolute z-10 mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-lg">
            <div className="p-2 border-b border-slate-200">
              <input value={query} onChange={(e) => setQuery(e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-md" placeholder="Escribe para filtrar" />
            </div>
            <div className="max-h-56 overflow-auto">
              {filtered.map(opt => (
                <button key={opt.value} className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${opt.value === value ? 'bg-slate-100' : ''}`} onClick={() => { onChange(opt.value); setOpen(false); }}>
                  {opt.label}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-slate-500">Sin resultados</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };
  const tareasFiltradas = tasks
    .filter(t => !esOnboarding(t))
    .filter(task => {
      if (filtroEstado === 'TODOS') return true;
      return task.estado === filtroEstado;
    })
    .filter(task => {
      const q = norm(busquedaProyecto);
      if (!q) return true;
      return norm(task?.proyecto?.nombre || '')?.includes(q);
    })
    .filter(task => {
      if (!filtroColaborador) return true;
      return String(task.colaborador || '') === String(filtroColaborador);
    });

  // Agrupar tareas por proyecto
  const tareasAgrupadasPorProyecto = tareasFiltradas.reduce((acc, task) => {
    const proyectoNombre = task.proyecto.nombre;
    if (!acc[proyectoNombre]) {
      acc[proyectoNombre] = [];
    }
    acc[proyectoNombre].push(task);
    return acc;
  }, {});

  const toInputDate = (s) => {
    const a = String(s || '');
    const part = a.split('T')[0].split(' ')[0];
    return part || '';
  };

  const handleVerDetalle = async (task) => {
    setSelectedTask(task);
    setShowModal(true);
    setModalLoading(true);
    setTaskDetail(null);
    setRevisionDate(toInputDate(task.fecha_entrega));
    setShowRevisionDate(false);
    try {
      let d = null;
      if (task.id_compromiso) {
        d = await fetchAuth(`/api/entregas/${task.id_compromiso}/`);
      } else {
        d = await fetchAuth(`/api/jefe/tareas/${task.id}/detalle/`);
      }
      setTaskDetail(d);
    } catch (e) {
      console.error('Error cargando detalle de tarea:', e);
    } finally {
      setModalLoading(false);
    }
  };

  const abrirArchivo = (url) => {
    if (!url) return;
    try { window.open(url, '_blank'); } catch {}
  };

  const getMimeFromUrl = (u) => {
    const s = String(u || '').toLowerCase();
    if (s.endsWith('.pdf')) return 'application/pdf';
    if (s.endsWith('.png')) return 'image/png';
    if (s.endsWith('.jpg') || s.endsWith('.jpeg')) return 'image/jpeg';
    if (s.endsWith('.zip')) return 'application/zip';
    if (s.endsWith('.doc')) return 'application/msword';
    if (s.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (s.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (s.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return 'application/octet-stream';
  };

  const openEvidence = async (url) => {
    try {
      const res = await fetch(url);
      const buf = await res.blob();
      const mime = getMimeFromUrl(url);
      const blob = buf.type && buf.type !== 'application/octet-stream' ? buf : new Blob([buf], { type: mime });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch {}
  };

  const downloadEvidence = async (url) => {
    try {
      const res = await fetch(url);
      const buf = await res.blob();
      const mime = getMimeFromUrl(url);
      const blob = buf.type && buf.type !== 'application/octet-stream' ? buf : new Blob([buf], { type: mime });
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      const parts = String(url || '').split('/');
      const fname = parts[parts.length - 1] || 'evidencia';
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch {}
  };

  const extractEvidenceUrl = (d) => {
    try {
      if (d?.entrega?.archivo_url) return String(d.entrega.archivo_url).trim().replace('//storage','/storage').replace('/object/public/evidencias/entregas/','/object/public/entregas/').replace('/object/public/entregas/entregas/','/object/public/entregas/');
      if (d?.evidencia_url) return String(d.evidencia_url).trim().replace('//storage','/storage').replace('/object/public/evidencias/entregas/','/object/public/entregas/').replace('/object/public/entregas/entregas/','/object/public/entregas/');
      const desc = String((d && d.descripcion) || '');
      const m = desc.match(/\[ENTREGA_EVIDENCIA_URL:([^\]]+)\]/);
      if (m && m[1]) return String(m[1]).trim();
    } catch {}
    return null;
  };

  const parseEntregaMeta = (d) => {
    const comentarios = String(d?.entrega?.comentario || '').trim();
    const fecha = String(d?.entrega?.fecha_entrega || '').trim();
    const evidencia = extractEvidenceUrl(d);
    const desc = String(d?.tarea?.descripcion || d?.descripcion || '').replace(/\[[^\]]+\]/g, '').trim();
    return { comentarios, fecha, evidencia, descripcion: desc };
  };

  const isImage = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(String(url || ''));

  const handleApprove = async (taskId) => {
    try {
      setProcessingId(taskId);
      if (selectedTask?.estado !== 'EN_REVISION') {
        setToast({ visible: true, type: 'error', title: 'Acción no permitida', message: 'Solo puedes aprobar tareas en revisión.' });
        return;
      }
      const rid = taskDetail?.revision?.id_revision;
      if (rid) {
        await fetchAuth(`/api/revision/${rid}/aprobar/`, { method: 'POST', body: JSON.stringify({ comentario: approveComment }) });
      } else {
        await fetchAuth(`/api/jefe/tareas/${taskId}/actualizar/`, { method: 'POST', body: JSON.stringify({ estado: 'Aprobado', comentario: approveComment }) });
      }
      setTasks(tasks.map(t => t.id === taskId ? { ...t, estado: 'APROBADO' } : t));
      await cargarTareas();
      if (selectedTask?.id === taskId) setShowModal(false);
      setToast({ visible: true, type: 'success', title: 'Tarea aprobada', message: 'La tarea fue aprobada.' });
    } catch (err) {
      console.error('Error aprobando tarea:', err);
      setToast({ visible: true, type: 'error', title: 'No se pudo aprobar', message: err.message || 'Intenta nuevamente.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (taskId) => {
    try {
      setProcessingId(taskId);
      if (selectedTask?.estado !== 'EN_REVISION') {
        setToast({ visible: true, type: 'error', title: 'Acción no permitida', message: 'Solo puedes devolver tareas en revisión.' });
        return;
      }
      const rid = taskDetail?.revision?.id_revision;
      if (rid) {
        await fetchAuth(`/api/revision/${rid}/rechazar/`, { method: 'POST', body: JSON.stringify({ comentario: approveComment }) });
      } else {
        await fetchAuth(`/api/jefe/tareas/${taskId}/actualizar/`, { method: 'POST', body: JSON.stringify({ estado: 'Rechazado' }) });
      }
      setTasks(tasks.map(t => t.id === taskId ? { ...t, estado: 'RECHAZADO' } : t));
      await cargarTareas();
      if (selectedTask?.id === taskId) setShowModal(false);
      setToast({ visible: true, type: 'success', title: 'Tarea rechazada', message: 'La tarea fue rechazada.' });
    } catch (err) {
      console.error('Error rechazando tarea:', err);
      setToast({ visible: true, type: 'error', title: 'No se pudo rechazar', message: err.message || 'Intenta nuevamente.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleSolicitarRevision = async (taskId) => {
    try {
      setProcessingId(taskId);
      if (!showRevisionDate) {
        setShowRevisionDate(true);
        if (!revisionDate) {
          setRevisionDate(toInputDate(selectedTask?.fecha_entrega));
        }
        return;
      }
      if (!revisionDate) {
        setToast({ visible: true, type: 'error', title: 'Falta fecha', message: 'Selecciona la nueva fecha de entrega.' });
        return;
      }
      await fetchAuth(`/api/jefe/tareas/${taskId}/actualizar/`, {
        method: 'POST',
        body: JSON.stringify({ estado: 'En Revisión', fecha_termino: revisionDate })
      });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, estado: 'EN_REVISION', fecha_entrega: revisionDate } : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, estado: 'EN_REVISION', fecha_entrega: revisionDate });
      }
      setToast({ visible: true, type: 'success', title: 'Revisión solicitada', message: 'Se solicitó revisión con nueva fecha.' });
    } catch (err) {
      console.error('Error solicitando revisión:', err);
      setToast({ visible: true, type: 'error', title: 'No se pudo solicitar', message: err.message || 'Intenta nuevamente.' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleAceptarNuevaFecha = async (taskId) => {
    try {
      setAcceptingDate(true);
      if (!revisionDate) {
        setToast({ visible: true, type: 'error', title: 'Falta fecha', message: 'Selecciona la nueva fecha de entrega.' });
        return;
      }
      await fetchAuth(`/api/jefe/tareas/${taskId}/actualizar/`, {
        method: 'POST',
        body: JSON.stringify({ estado: 'En Progreso', fecha_termino: revisionDate })
      });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, estado: 'EN_PROGRESO', fecha_entrega: revisionDate } : t));
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, estado: 'EN_PROGRESO', fecha_entrega: revisionDate });
      }
      setShowRevisionDate(false);
      cerrarModal();
      setToast({ visible: true, type: 'success', title: 'Fecha actualizada', message: 'Se aceptó la nueva fecha de entrega.' });
    } catch (err) {
      console.error('Error aceptando nueva fecha:', err);
      setToast({ visible: true, type: 'error', title: 'No se pudo actualizar', message: err.message || 'Intenta nuevamente.' });
    } finally {
      setAcceptingDate(false);
    }
  };

  const cerrarModal = () => {
    setShowModal(false);
    setSelectedTask(null);
    setTaskDetail(null);
    setApproveComment('');
  };

  const getEstadoStyles = (estado) => {
    switch (estado) {
      case 'EN_REVISION':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'EN_PROGRESO':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'APROBADO':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'RECHAZADO':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'EN_REVISION': return 'En Revisión';
      case 'EN_PROGRESO': return 'En Progreso';
      case 'APROBADO': return 'Aprobada';
      case 'RECHAZADO': return 'Rechazada';
      default: return estado || 'Pendiente';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-indigo-600 text-5xl mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Cargando tareas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-md">
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100 p-6">
      <AnimatePresence>
        {toast.visible && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.98 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <div className={`flex items-start gap-3 rounded-xl border shadow-lg px-4 py-3 ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`${toast.type === 'success' ? 'text-emerald-600' : 'text-red-600'} text-xl`}>
                  {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">{toast.title}</p>
                  <p className="text-sm text-slate-600">{toast.message}</p>
                </div>
                <button
                  onClick={() => setToast(prev => ({ ...prev, visible: false }))}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Cerrar notificación"
                >
                  <FaTimes />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 rounded-2xl shadow-lg shadow-indigo-500/30">
            <FaClipboardCheck className="text-white text-3xl" />
          </div>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Tareas por Aprobar
            </h1>
            <p className="text-slate-600 mt-1">Revisa y gestiona las tareas pendientes de tus proyectos</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div 
          className={`bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-all cursor-pointer ${
            filtroEstado === 'TODOS' ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-slate-200'
          }`}
          onClick={() => setFiltroEstado('TODOS')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="bg-blue-100 p-3 rounded-xl">
              <FaClipboardCheck className="text-blue-600 text-2xl" />
            </div>
            <span className="text-3xl font-bold text-blue-600">{stats.total}</span>
          </div>
          <p className="text-slate-600 font-medium">Total Pendientes</p>
        </div>

        <div 
          className={`bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-all cursor-pointer ${
            filtroEstado === 'EN_REVISION' ? 'border-amber-400 ring-2 ring-amber-200' : 'border-slate-200'
          }`}
          onClick={() => setFiltroEstado('EN_REVISION')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="bg-amber-100 p-3 rounded-xl">
              <FaHourglassHalf className="text-amber-600 text-2xl" />
            </div>
            <span className="text-3xl font-bold text-amber-600">{stats.enRevision}</span>
          </div>
          <p className="text-slate-600 font-medium">En Revisión</p>
        </div>

        <div 
          className={`bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-all cursor-pointer ${
            filtroEstado === 'APROBADO' ? 'border-green-400 ring-2 ring-green-200' : 'border-slate-200'
          }`}
          onClick={() => setFiltroEstado('APROBADO')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="bg-green-100 p-3 rounded-xl">
              <FaUserCheck className="text-green-600 text-2xl" />
            </div>
            <span className="text-3xl font-bold text-green-600">{stats.aprobadas}</span>
          </div>
          <p className="text-slate-600 font-medium">Aprobadas</p>
        </div>

        <div 
          className={`bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-all cursor-pointer ${
            filtroEstado === 'RECHAZADO' ? 'border-red-400 ring-2 ring-red-200' : 'border-slate-200'
          }`}
          onClick={() => setFiltroEstado('RECHAZADO')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="bg-red-100 p-3 rounded-xl">
              <FaBan className="text-red-600 text-2xl" />
            </div>
            <span className="text-3xl font-bold text-red-600">{stats.rechazadas}</span>
          </div>
          <p className="text-slate-600 font-medium">Rechazadas</p>
        </div>
      </div>


      <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <SearchableSelect
            value={busquedaProyecto}
            onChange={setBusquedaProyecto}
            options={[{ value: '', label: 'Todos los proyectos' }, ...proyectosUnicos.map(p => ({ value: p, label: p }))]}
            placeholder="Todos los proyectos"
            className="min-w-[220px]"
          />
          <SearchableSelect
            value={filtroColaborador}
            onChange={setFiltroColaborador}
            options={[{ value: '', label: 'Todos los colaboradores' }, ...colaboradoresUnicos.map(c => ({ value: c, label: c }))]}
            placeholder="Todos los colaboradores"
            className="min-w-[220px]"
          />
          <SearchableSelect
            value={filtroEstado}
            onChange={setFiltroEstado}
            options={estadoOptions}
            placeholder="Todos los estados"
            className="min-w-[220px]"
          />
          <button
            onClick={() => { setBusquedaProyecto(''); setFiltroColaborador(''); setFiltroEstado('TODOS'); }}
            className="ml-auto px-3 py-2 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Limpiar filtros
          </button>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-amber-100 p-3 rounded-xl">
            <FaHourglassHalf className="text-amber-600 text-2xl" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Solicitudes de Plazo</h2>
          {solLoading && <span className="text-sm text-slate-500">Cargando...</span>}
          {!solLoading && <span className="ml-auto text-sm font-semibold text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">{solicitudes.length} pendientes</span>}
        </div>
        {solError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{solError}</div>
        )}
        {(!solLoading && solicitudes.length === 0) ? (
          <div className="bg-white rounded-2xl p-6 border border-slate-200 text-slate-600">No hay solicitudes pendientes</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {solicitudes.map(s => (
              <div key={s.id} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-bold text-slate-800">{s.tarea_nombre || 'Tarea'}</h3>
                    <p className="text-sm text-slate-600">{s.colaborador_nombre || 'Colaborador'}</p>
                    <div className="mt-1 text-xs text-slate-600 flex items-center gap-2">
                      <FaProjectDiagram className="text-[10px] text-slate-500" />
                      <span>{(s.proyecto && s.proyecto.nombre_proyecto) ? s.proyecto.nombre_proyecto : 'Proyecto no disponible'}</span>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded-lg text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200">Pendiente</span>
                </div>
                {s.proyecto && (s.proyecto.fecha_inicio || s.proyecto.fecha_fin || s.proyecto.estado) && (
                  <div className="flex items-center gap-2 mb-3">
                    {s.proyecto.estado && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border text-xs">
                        <FaCheck className="text-[10px]" />
                        <span>{s.proyecto.estado}</span>
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border text-xs">
                      <FaCalendar className="text-[10px]" />
                      <span>Inicio: {s.proyecto.fecha_inicio ? formatDate(s.proyecto.fecha_inicio) : '-'}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border text-xs">
                      <FaCalendar className="text-[10px]" />
                      <span>Término: {s.proyecto.fecha_fin ? formatDate(s.proyecto.fecha_fin) : '-'}</span>
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                    <p className="text-xs text-slate-500">Fecha actual</p>
                    <p className="text-sm font-semibold text-slate-800">{s.fecha_actual ? formatDate(s.fecha_actual) : 'Sin fecha'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                    <p className="text-xs text-slate-500">Nueva fecha</p>
                    <p className="text-sm font-semibold text-slate-800">{s.nueva_fecha ? formatDate(s.nueva_fecha) : '-'}</p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Motivo</p>
                  <p className="text-sm text-slate-700">{s.motivo}</p>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Comentario</p>
                  <textarea
                    value={comentariosSolicitud[s.id] || ''}
                    onChange={(e) => setComentariosSolicitud(prev => ({ ...prev, [s.id]: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    placeholder="Escribe tu comentario para el colaborador"
                  />
                </div>
                {s.proyecto && s.proyecto.id_proyecto && (
                  <div className="mb-3">
                    <Link
                      to={`/app/jefe/proyectos/${s.proyecto.id_proyecto}`}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm font-semibold"
                    >
                      <FaExternalLinkAlt /> Ver proyecto
                    </Link>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    disabled={respondingId === s.id}
                    onClick={() => responderSolicitud(s.id, 'APROBADO')}
                    className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg"
                  >
                    {respondingId === s.id ? 'Procesando...' : 'Aceptar'}
                  </button>
                  <button
                    disabled={respondingId === s.id}
                    onClick={() => responderSolicitud(s.id, 'RECHAZADA')}
                    className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg"
                  >
                    {respondingId === s.id ? 'Procesando...' : 'Rechazar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tasks List Agrupadas por Proyecto */}
      {tareasFiltradas.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <FaClipboardCheck className="text-slate-300 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No hay tareas con este filtro</h3>
          <p className="text-slate-500">Haz clic en las estadísticas para filtrar las tareas</p>
        </div>
      ) : (
        // Vista agrupada por proyecto
        <div className="space-y-8">
          {Object.entries(tareasAgrupadasPorProyecto)
            .sort(([, a], [, b]) => {
              const up = (s) => String(s || '').toUpperCase();
              const aRev = a.filter(t => up(t.estado) === 'EN_REVISION').length;
              const bRev = b.filter(t => up(t.estado) === 'EN_REVISION').length;
              if (bRev !== aRev) return bRev - aRev;
              return (b.length - a.length);
            })
            .map(([proyectoNombre, tareas]) => (
              <div key={proyectoNombre} className="space-y-4">
              {/* Header del Proyecto */}
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl p-4 shadow-lg shadow-indigo-500/30">
                <div className="flex items-center gap-3 text-white">
                  <FaProjectDiagram className="text-2xl" />
                  <h2 className="text-xl font-bold">{proyectoNombre}</h2>
                  {(() => {
                    const up = (s) => String(s || '').toUpperCase();
                    const total = tareas.length;
                    const completadas = tareas.filter(t => ['APROBADO','COMPLETADO'].includes(up(t.estado))).length;
                    const pendientes = tareas.filter(t => up(t.estado) === 'PENDIENTE').length;
                    const enProgreso = tareas.filter(t => up(t.estado) === 'EN_PROGRESO').length;
                    const enRevision = tareas.filter(t => up(t.estado) === 'EN_REVISION').length;
                    return (
                      <div className="hidden md:flex items-center gap-2 ml-auto">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-white text-sm">
                          <FaTasks className="opacity-90" />
                          <span>Total</span>
                          <span className="ml-2 font-bold">{total}</span>
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-white text-sm">
                          <FaUserCheck className="opacity-90" />
                          <span>Completadas</span>
                          <span className="ml-2 font-bold">{completadas}</span>
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-white text-sm">
                          <FaHourglassHalf className="opacity-90" />
                          <span>Pendientes</span>
                          <span className="ml-2 font-bold">{pendientes}</span>
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-white text-sm">
                          <FaSpinner className="opacity-90" />
                          <span>En Progreso</span>
                          <span className="ml-2 font-bold">{enProgreso}</span>
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-white/10 border border-white/20 text-white text-sm">
                          <FaHourglassHalf className="opacity-90" />
                          <span>En Revisión</span>
                          <span className="ml-2 font-bold">{enRevision}</span>
                        </span>
                      </div>
                    );
                  })()}
                  {(() => {
                    const isCollapsed = (collapsedProjects[proyectoNombre] ?? true);
                    return (
                      <button
                        onClick={() => setCollapsedProjects(prev => ({ ...prev, [proyectoNombre]: !isCollapsed }))}
                        className="ml-3 inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-semibold border border-white/20"
                      >
                        {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                        {isCollapsed ? 'Mostrar' : 'Ocultar'}
                      </button>
                    );
                  })()}
                </div>
              </div>
              
              {/* Tareas del Proyecto */}
              {((collapsedProjects[proyectoNombre] ?? true) === false) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 pl-4">
                  {tareas.map((task) => (
                    <div
                      key={task.id}
                      className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-lg hover:border-indigo-300 transition-all cursor-pointer group"
                      onClick={() => handleVerDetalle(task)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-slate-800 mb-1 group-hover:text-indigo-600 transition-colors">
                            {task.nombre}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <FaUser className="text-slate-400" />
                            <span>{typeof task.colaborador === 'object' ? (task.colaborador.nombre || 'Sin asignar') : (task.colaborador || 'Sin asignar')}</span>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-lg text-xs font-semibold border ${getEstadoStyles(task.estado)}`}>
                          {getEstadoTexto(task.estado)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-600 mb-3 pb-3 border-b border-slate-100">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border">
                          <FaCalendar className="text-[10px]" />
                          <span>Entrega: {(task.fecha_entrega_inicial || task.fecha_entrega) ? formatDate(task.fecha_entrega_inicial || task.fecha_entrega) : '-'}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border">
                          <FaHourglassHalf className="text-[10px]" />
                          <span>Plazo: {task.fecha_plazo ? formatDate(task.fecha_plazo) : '-'}</span>
                        </span>
                        {(() => {
                          const ep = String(task.estado_plazo || '').toUpperCase()
                          const cls = ep === 'APROBADO' ? 'bg-emerald-100 text-emerald-700' : ep === 'RECHAZADO' ? 'bg-red-100 text-red-700' : ep === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                          const label = task.estado_plazo ? (ep === 'APROBADO' ? 'Plazo aceptado' : ep === 'RECHAZADO' ? 'Plazo rechazado' : 'Pendiente') : '-'
                          return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{label}</span>
                        })()}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleVerDetalle(task); }}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-sm font-semibold rounded-lg transition-all shadow-sm"
                        >
                          <FaInfoCircle /> Ver detalle
                        </button>
                      </div>
                    </div>
            ))}
        </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de Detalle */}
      {showModal && selectedTask && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">{selectedTask.nombre}</h2>
                  <div className="flex items-center gap-3 text-indigo-100">
                    <div className="flex items-center gap-2">
                      <FaUser />
                      <span>{typeof selectedTask.colaborador === 'object' ? (selectedTask.colaborador.nombre || 'Sin asignar') : (selectedTask.colaborador || 'Sin asignar')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaProjectDiagram />
                      <span>{selectedTask.proyecto.nombre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaHourglassHalf />
                      <span>{taskDetail?.etapa?.nombre || 'Sin etapa'}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={cerrarModal}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {modalLoading ? (
                <div className="text-center py-12">
                  <FaSpinner className="animate-spin text-indigo-600 text-4xl mx-auto mb-4" />
                  <p className="text-slate-600">Cargando detalles...</p>
                </div>
              ) : taskDetail ? (
                <div className="space-y-6">
                  {/* Estado y Fecha */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-sm text-slate-600 mb-2">Estado</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getEstadoStyles(selectedTask.estado)}`}>
                        {getEstadoTexto(selectedTask.estado)}
                      </span>
                    </div>
                    {selectedTask.fecha_entrega && (
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-sm text-slate-600 mb-2">Fecha de Entrega</p>
                        <p className="font-semibold text-slate-800">{formatDate(selectedTask.fecha_entrega)}</p>
                      </div>
                    )}
                  </div>

                  {(['EN_REVISION','PENDIENTE','EN_PROGRESO'].includes(selectedTask.estado)) && null}

                  {null}

                  {(['EN_REVISION','PENDIENTE','EN_PROGRESO'].includes(selectedTask.estado)) && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-sm text-slate-600 mb-2">Comentario del jefe</p>
                      <textarea
                        rows={3}
                        value={approveComment}
                        onChange={(e) => setApproveComment(e.target.value)}
                        className="w-full border border-slate-300 rounded-xl px-3 py-2 text-slate-800"
                        placeholder="Opcional: se enviará al colaborador por correo"
                      />
                    </div>
                  )}

                  {(() => {
                    if (!taskDetail) return null;
                    const meta = parseEntregaMeta(taskDetail);
                    return (
                      <>
                        {meta.descripcion && (
                          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                            <div className="flex items-center gap-2 mb-3">
                              <FaInfoCircle className="text-slate-600" />
                              <h3 className="font-bold text-slate-800">Descripción</h3>
                            </div>
                            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                              {meta.descripcion}
                            </p>
                          </div>
                        )}
                        {(meta.comentarios || meta.fecha) && (
                          <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                            <h3 className="font-bold text-indigo-900 mb-2">Entrega del Colaborador</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {meta.comentarios && (
                                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                                  <p className="text-xs text-slate-500 mb-1">Comentarios</p>
                                  <p className="text-slate-800 whitespace-pre-wrap">{meta.comentarios}</p>
                                </div>
                              )}
                              {meta.fecha && (
                                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                                  <p className="text-xs text-slate-500 mb-1">Fecha de entrega del colaborador</p>
                                  <p className="text-slate-800 font-semibold">
                                    {formatDate(meta.fecha)}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {(() => {
                    const url = extractEvidenceUrl(taskDetail);
                    return (
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <div className="flex items-center gap-2 mb-3">
                          <FaPaperclip className="text-slate-600" />
                          <h3 className="font-bold text-slate-800">Archivo adjunto</h3>
                        </div>
                        {!url ? (
                          <p className="text-slate-600 text-sm">No hay archivo adjunto disponible</p>
                        ) : isImage(url) ? (
                          <div className="flex items-start gap-3">
                            <img src={url} alt="Adjunto" className="max-h-48 rounded-lg border" />
                            <div className="flex flex-col gap-2">
                              <button onClick={() => downloadEvidence(url)} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl">
                                <FaDownload /> Descargar evidencia
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => downloadEvidence(url)} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl">
                              <FaDownload /> Descargar evidencia
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-600">No se pudieron cargar los detalles</p>
                </div>
              )}
            </div>

            <div className="bg-slate-50 p-6 border-t border-slate-200 flex justify-end gap-3">
              {(selectedTask?.estado === 'EN_REVISION') && (
                <div className="flex flex-wrap items-center gap-3 mr-auto">
                  <button
                    onClick={() => handleApprove(selectedTask.id)}
                    disabled={processingId === selectedTask.id}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {processingId === selectedTask.id ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                    Aprobar
                  </button>
                  <button
                    onClick={() => handleReject(selectedTask.id)}
                    disabled={processingId === selectedTask.id}
                    className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {processingId === selectedTask.id ? <FaSpinner className="animate-spin" /> : <FaTimes />}
                    Devolver
                  </button>
                </div>
              )}
              <button
                onClick={cerrarModal}
                className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '-'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }
